import './style.scss';

import { useEffect, useRef, useState } from 'react';

const NO_HIGHLIGHTED_OPTION = -1;
const FIRST_OPTION_INDEX = 0;
const NEXT_OPTION = 1;
const PREVIOUS_OPTION = -1;

export interface IComboBoxProps {
    ariaLabel?: string;
    ariaDescribedBy?: string;
    name: string;
    options: string[];
    placeholder?: string;
    defaultValue?: string;
    optionModifier?: string;
    onSelect?: (evt: any) => void;
    selectedOption: string;
    multiselectable?: boolean;
    selectedOptions?: Array<string>;
    disabled?: boolean;
    prefix?: string;
    shouldDisplayOptionName?: boolean;
    hasError?: boolean;
    customClass?: string;
}
export interface IComboBoxOptionProps {
    parentName: string;
    optionName: string;
    index: number;
    onSelect: any;
    selected: boolean;
    active?: boolean;
    displayName?: string;
    ariaActive?: boolean;
}

export const ComboBoxOption: React.FC<IComboBoxOptionProps> = ({
    parentName,
    optionName,
    index,
    onSelect,
    selected,
    active = false,
    displayName,
    ariaActive = false,
}) => {
    return (
        <div
            role='option'
            id={`${parentName}-option-${index}`}
            className={`c-combobox__item combobox-item ${
                active ? 'is-active' : ''
            } ${ariaActive ? ' is-highlighted' : ''}`}
            data-value={`${optionName
                .toString()
                .replace(/ /, '_')
                .toLowerCase()}`}
            aria-selected={selected}
            data-active={ariaActive}
            aria-disabled='false'
            onClick={() => onSelect(optionName)}
        >
            <div className='c-combobox__item__text'>
                {displayName ?? optionName}
            </div>
        </div>
    );
};

export const ComboBox: React.FC<IComboBoxProps> = ({
    ariaLabel,
    ariaDescribedBy,
    name,
    options,
    placeholder = '',
    defaultValue = '',
    onSelect,
    selectedOption,
    optionModifier,
    multiselectable = false,
    selectedOptions,
    disabled = false,
    prefix,
    shouldDisplayOptionName,
    hasError = false,
    customClass = '',
}) => {
    const NO_ACTIVE_OPTION = -1;
    const [visible, setVisible] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number>(NO_ACTIVE_OPTION);
    const [highlightedIndex, setHighlightedIndex] = useState(
        NO_HIGHLIGHTED_OPTION,
    );
    const listboxRef = useRef<HTMLDivElement>(null);
    const disabledClass = disabled ? 'disabled' : '';

    useEffect(() => {
        if (!visible || highlightedIndex < FIRST_OPTION_INDEX) return;

        listboxRef.current
            ?.querySelector<HTMLElement>('.is-highlighted')
            ?.scrollIntoView?.({ block: 'nearest' });
    }, [activeIndex, highlightedIndex, options, visible]);

    const handleClick = () => {
        if (!disabled) {
            setVisible(!visible);
            setHighlightedIndex(options.indexOf(selectedOption));
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled || !options.length) return;

        if (event.key === 'Escape') {
            event.preventDefault();
            setVisible(false);
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (!visible) {
                setVisible(true);
                setHighlightedIndex(options.indexOf(selectedOption));
            } else if (highlightedIndex >= FIRST_OPTION_INDEX) {
                onSelect?.(options[highlightedIndex]);
                setVisible(false);
            }
            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const direction =
                event.key === 'ArrowDown' ? NEXT_OPTION : PREVIOUS_OPTION;
            const currentIndex = highlightedIndex;
            const nextIndex =
                (currentIndex + direction + options.length) % options.length;
            setHighlightedIndex(nextIndex);
            setVisible(true);
        }
    };

    const displayedValue = (): string => {
        let value = '';
        if (!selectedOption && (!!placeholder || !!defaultValue)) {
            return placeholder || defaultValue;
        } else if (!!optionModifier) {
            value = `${selectedOption} ${optionModifier}`;
        } else {
            value = selectedOption;
        }
        return prefix ? `${prefix} ${value}` : value;
    };

    return (
        <div className={`c-combobox__wrapper  js-combobox ${customClass}`}>
            <input
                type='hidden'
                name='combobox-values'
                className='js-combobox__input-values'
                disabled={disabled}
            ></input>
            <div
                role='combobox'
                aria-label={ariaLabel}
                aria-describedby={ariaDescribedBy}
                aria-invalid={hasError || undefined}
                aria-haspopup='listbox'
                aria-expanded={visible}
                aria-owns={`${name}-listbox`}
                aria-controls={`${name}-listbox`}
                aria-activedescendant={
                    highlightedIndex >= FIRST_OPTION_INDEX
                        ? `${name}-option-${highlightedIndex}`
                        : activeIndex !== NO_ACTIVE_OPTION
                        ? `${name}-option-${activeIndex}`
                        : ''
                }
                tabIndex={0}
                id={name}
                className={`c-combobox ${hasError ? 'isError' : ''}`}
                aria-disabled={disabled}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                onBlur={() => {
                    setVisible(false);
                    setActiveIndex(NO_ACTIVE_OPTION);
                }}
            >
                <div className='c-combobox__value-presentation  js-combobox-value-presentation'>
                    <span
                        id={`${name}-values`}
                        className={
                            'c-combobox__value-presentation__text  has-placeholder' +
                            disabledClass
                        }
                    >
                        {displayedValue()}
                    </span>
                </div>

                <div className='c-combobox__listbox-container'>
                    <div
                        id={`${name}-listbox`}
                        className='c-combobox__listbox'
                        role='listbox'
                        aria-multiselectable={`${multiselectable}`}
                        aria-disabled='false'
                        ref={listboxRef}
                    >
                        {options.map((option, index) => {
                            if (option) {
                                const selectedOp =
                                    multiselectable && selectedOptions
                                        ? selectedOptions.includes(option)
                                        : selectedOption === option;
                                return (
                                    <ComboBoxOption
                                        key={option}
                                        parentName={name}
                                        optionName={option}
                                        index={index}
                                        onSelect={onSelect}
                                        selected={selectedOp}
                                        active={activeIndex === index}
                                        displayName={
                                            shouldDisplayOptionName
                                                ? `${prefix ?? ''} ${option} ${
                                                      optionModifier ?? ''
                                                  }`.trim()
                                                : undefined
                                        }
                                        ariaActive={highlightedIndex === index}
                                    />
                                );
                            }
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
